/** @jsx jsx */
import { SyntheticEvent } from 'react';
import { Link } from '@zapier/common-components';
import { Border, Colors } from '@zapier/theme';
import { css, jsx } from '@emotion/core';
import GoogleIcon from './GoogleIcon';

type GoogleButtonProps = {
  /** URL button links to */
  href?: string;

  /** True to make button blue */
  isBlue?: boolean;

  /** True to make button large */
  isLarge?: boolean;

  /** Button Text */
  label: string;

  /** Called on user click */
  onClick?: (event: SyntheticEvent<HTMLAnchorElement>) => void;
};

const buttonCss = (props: GoogleButtonProps) =>
  css(
    {
      alignItems: 'center',
      backgroundColor: Colors.white,
      border: `${Border.size.default} solid ${Border.color.default}`,
      borderRadius: Border.radius.default,
      // From Google Guidelines
      boxShadow: '0 0 1px rgba(0,0,0,0.12), 0 1px 1px rgba(0,0,0,0.24)',
      color: Colors.deepSpace,
      display: 'flex',
      fontSize: 13,
      padding: 10,
      position: 'relative',
      transition: 'all 100ms ease',

      '&:hover, &:focus': {
        background: Colors.white,
        filter: 'brightness(95%)',
        color: Colors.deepSpace,
        textDecoration: 'none',
      },
    },
    props.isLarge && {
      fontSize: 15,
      fontWeight: 600,
      padding: 15,
    },
    props.isBlue && {
      backgroundColor: '#4285f4', // From Google Guidelines
      border: 'none',
      color: Colors.white,
      '&::before': {
        backgroundColor: Colors.white,
        borderRadius: 1,
        bottom: 2,
        content: '""',
        left: 2,
        position: 'absolute',
        top: 2,
        width: 36,
      },
      '&:hover, &:focus': {
        backgroundColor: '#3367d6', // From Google Guidelines
        color: Colors.white,
      },
    },
    props.isBlue &&
      props.isLarge && {
        '&::before': {
          width: 46,
        },
      }
  );

const iconCss = {
  height: 20,
  width: 20,
  // Relative, just to be above the background when --blue is active
  zIndex: 1,
};

const labelCss = (props: GoogleButtonProps) =>
  css(
    {
      flex: 1,
      marginLeft: -18, // compensate the logo to look like centered
      textAlign: 'center',
    },
    props.isBlue && {
      margin: '0 18px',
    }
  );

const GoogleButton = (props: GoogleButtonProps) => {
  const { href, label, onClick } = props;
  return (
    <Link href={href} onClick={onClick} underline="never">
      <div css={buttonCss(props)}>
        <GoogleIcon css={iconCss} />
        <span css={labelCss}>{label}</span>
      </div>
    </Link>
  );
};

export default GoogleButton;
